import React, { useState, useContext, useMemo } from 'react';
import type { PromptVersion } from '../types';
import { v4 as uuidv4 } from 'uuid';
import { ToastContext } from '../App';
import Modal from './Modal';
import { LayersIcon, LayersPlusIcon, SaveIcon, RestoreIcon, TrashIcon, ClipboardCopyIcon } from '../constants';

interface VersionHistoryProps {
  versions: PromptVersion[];
  content: string;
  onChangeVersions: (versions: PromptVersion[]) => void;
  onChangeContent: (content: string) => void;
}

const VersionHistory: React.FC<VersionHistoryProps> = ({ versions, content, onChangeVersions, onChangeContent }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newVersionName, setNewVersionName] = useState('');
  const [expandedDiffId, setExpandedDiffId] = useState<string | null>(null);
  const { showToast } = useContext(ToastContext);

  /** Compute word-level diff within lines */
  const diffWords = (oldStr: string, newStr: string) => {
    const oldWords = oldStr.split(/(\s+)/).filter(Boolean);
    const newWords = newStr.split(/(\s+)/).filter(Boolean);

    const m = oldWords.length;
    const n = newWords.length;
    const dp: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));

    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (oldWords[i - 1] === newWords[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1] + 1;
        } else {
          dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
        }
      }
    }

    const path: { type: 'same' | 'added' | 'removed'; value: string }[] = [];
    let i = m, j = n;

    while (i > 0 && j > 0) {
      if (oldWords[i - 1] === newWords[j - 1]) {
        path.push({ type: 'same', value: oldWords[i - 1] });
        i--;
        j--;
      } else if (dp[i - 1][j] > dp[i][j - 1]) {
        path.push({ type: 'removed', value: oldWords[i - 1] });
        i--;
      } else {
        path.push({ type: 'added', value: newWords[j - 1] });
        j--;
      }
    }

    while (i > 0) {
      path.push({ type: 'removed', value: oldWords[i - 1] });
      i--;
    }
    while (j > 0) {
      path.push({ type: 'added', value: newWords[j - 1] });
      j--;
    }

    return path.reverse();
  };

  /** Compute line-level diff between two texts using LCS, and word-level diff for modified lines */
  const computeDiff = (oldText: string, newText: string) => {
    const oldLines = oldText.split('\n');
    const newLines = newText.split('\n');

    const m = oldLines.length;
    const n = newLines.length;
    const dp: number[][] = Array(m + 1).fill(null).map(() => Array(n + 1).fill(0));

    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        if (oldLines[i - 1] === newLines[j - 1]) {
          dp[i][j] = dp[i - 1][j - 1] + 1;
        } else {
          dp[i][j] = Math.max(dp[i - 1][j], dp[i][j - 1]);
        }
      }
    }

    type LineOp = { type: 'same' | 'added' | 'removed'; oldLine?: string; newLine?: string };
    const lineOps: LineOp[] = [];
    let i = m, j = n;

    while (i > 0 && j > 0) {
      if (oldLines[i - 1] === newLines[j - 1]) {
        lineOps.push({ type: 'same', oldLine: oldLines[i - 1] });
        i--;
        j--;
      } else if (dp[i - 1][j] >= dp[i][j - 1]) {
        lineOps.push({ type: 'removed', oldLine: oldLines[i - 1] });
        i--;
      } else {
        lineOps.push({ type: 'added', newLine: newLines[j - 1] });
        j--;
      }
    }

    while (i > 0) {
      lineOps.push({ type: 'removed', oldLine: oldLines[i - 1] });
      i--;
    }
    while (j > 0) {
      lineOps.push({ type: 'added', newLine: newLines[j - 1] });
      j--;
    }

    lineOps.reverse();

    const result: {
      type: 'same' | 'added' | 'removed' | 'modified';
      chunks: { type: 'same' | 'added' | 'removed'; value: string }[];
    }[] = [];

    let k = 0;
    while (k < lineOps.length) {
      if (lineOps[k].type === 'same') {
        result.push({ type: 'same', chunks: [{ type: 'same', value: lineOps[k].oldLine! }] });
        k++;
      } else {
        const removed: string[] = [];
        const added: string[] = [];
        while (k < lineOps.length && lineOps[k].type !== 'same') {
          if (lineOps[k].type === 'removed') {
            removed.push(lineOps[k].oldLine!);
          } else {
            added.push(lineOps[k].newLine!);
          }
          k++;
        }
        const pairs = Math.min(removed.length, added.length);
        for (let p = 0; p < pairs; p++) {
          result.push({ type: 'modified', chunks: diffWords(removed[p], added[p]) });
        }
        for (let p = pairs; p < removed.length; p++) {
          result.push({ type: 'removed', chunks: [{ type: 'removed', value: removed[p] }] });
        }
        for (let p = pairs; p < added.length; p++) {
          result.push({ type: 'added', chunks: [{ type: 'added', value: added[p] }] });
        }
      }
    }
    return result;
  };

  const activeDiff = useMemo(() => {
    if (!expandedDiffId) return null;
    const v = (versions || []).find(item => item.id === expandedDiffId);
    if (!v) return null;
    return computeDiff(v.content, content);
  }, [expandedDiffId, versions, content]);

  const handleOpenSaveVersionModal = () => {
    const defaultName = `Version ${(versions || []).length + 1} - ${new Date().toLocaleDateString('de-DE')}`;
    setNewVersionName(defaultName);
    setIsModalOpen(true);
  };

  const handleSaveNewVersion = () => {
    if (!newVersionName.trim()) return;
    const newVersion: PromptVersion = {
      id: uuidv4(),
      name: newVersionName.trim(),
      content,
      date: new Date().toISOString(),
    };
    onChangeVersions([newVersion, ...versions]);
    setIsModalOpen(false);
    setNewVersionName('');
    showToast('Version gespeichert!');
  };

  const handleLoadVersion = (v: PromptVersion) => {
    if (content !== v.content && content.trim() !== '') {
      if (!window.confirm(`Möchten Sie die Version "${v.name}" wirklich laden? Der aktuelle Editor-Inhalt wird dabei überschrieben.`)) {
        return;
      }
    }
    onChangeContent(v.content);
    showToast(`Version "${v.name}" geladen.`);
  };

  const handleDeleteVersion = (id: string) => {
    onChangeVersions(versions.filter(v => v.id !== id));
    showToast('Version gelöscht.');
  };

  const handleCopyVersion = (versionContent: string) => {
    if (!navigator.clipboard?.writeText) {
      showToast('Zwischenablage in dieser Umgebung nicht verfügbar.', 'error');
      return;
    }
    navigator.clipboard.writeText(versionContent).then(() => {
      showToast('Version kopiert!');
    }).catch(() => {
      showToast('Fehler beim Kopieren.', 'error');
    });
  };

  const handleUpdateVersion = (id: string, field: 'name' | 'content', value: string) => {
    onChangeVersions(
      versions.map((version) =>
        version.id === id
          ? {
              ...version,
              [field]: value,
              date: new Date().toISOString(),
            }
          : version
      )
    );
  };

  return (
    <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-6">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 flex items-center gap-2">
          <LayersIcon className="h-[18px] w-[18px]" />
          <span>Versionen ({versions?.length || 0})</span>
        </h3>
        <button
          onClick={handleOpenSaveVersionModal}
          className="flex items-center gap-2 px-3 py-1.5 text-xs rounded-md font-medium bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600"
        >
          <LayersPlusIcon className="h-4 w-4" />
          Als Version speichern
        </button>
      </div>
      <div className="space-y-4">
        {versions && versions.length > 0 ? (
          versions.map(v => (
            <div key={v.id} className="p-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600">
              <div className="flex justify-between items-start gap-3">
                <div className="flex-grow">
                  <input
                    type="text"
                    value={v.name}
                    onChange={(e) => handleUpdateVersion(v.id, 'name', e.target.value)}
                    className="w-full bg-transparent text-sm font-semibold text-gray-800 dark:text-gray-100 focus:outline-none mb-1"
                    placeholder="Versionsname"
                    aria-label={`Versionsname ${v.name}`}
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400">Zuletzt bearbeitet am {new Date(v.date).toLocaleString('de-DE')}</p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => handleCopyVersion(v.content)} className="p-1.5 rounded-full text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700 dark:text-gray-400" title="Kopieren">
                    <ClipboardCopyIcon />
                  </button>
                  <button
                    onClick={() => setExpandedDiffId(expandedDiffId === v.id ? null : v.id)}
                    className={`flex items-center px-2 py-1 text-xs rounded transition-colors ${
                      expandedDiffId === v.id
                        ? 'bg-indigo-100 dark:bg-indigo-500/20 text-indigo-700 dark:text-indigo-300'
                        : 'bg-gray-200 dark:bg-gray-700 hover:bg-indigo-100 dark:hover:bg-indigo-500/20 hover:text-indigo-700'
                    }`}
                    title="Vergleich mit aktuellem Editor-Inhalt"
                  >
                    <svg className="w-3.5 h-3.5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                    </svg>
                    Vergleich
                  </button>
                  <button onClick={() => handleLoadVersion(v)} className="flex items-center px-2 py-1 text-xs rounded bg-gray-200 dark:bg-gray-700 hover:bg-indigo-500 hover:text-white transition-colors" title="Als Hauptprompt laden">
                    <RestoreIcon />
                    <span className="ml-1">Laden</span>
                  </button>
                  <button onClick={() => {
                    if (v.content !== content && v.content.trim() !== "" && !window.confirm(`Möchten Sie die Version "${v.name}" wirklich mit dem aktuellen Editor-Inhalt überschreiben? Ihre manuellen Änderungen an dieser Version gehen verloren.`)) return;
                    handleUpdateVersion(v.id, "content", content);
                    showToast(`Version "${v.name}" wurde mit Editor-Inhalt aktualisiert.`);
                  }} className="flex items-center px-2 py-1 text-xs rounded bg-gray-200 dark:bg-gray-700 hover:bg-amber-500 hover:text-white transition-colors" title="Mit aktuellem Editor-Inhalt überschreiben">
                    <SaveIcon />
                    <span className="ml-1">Vom Editor übernehmen</span>
                  </button>
                  <button onClick={() => handleDeleteVersion(v.id)} className="p-1.5 rounded-full text-gray-500 hover:bg-red-100 hover:text-red-600 dark:hover:bg-red-500/10 dark:text-gray-400" title="Löschen">
                    <TrashIcon />
                  </button>
                </div>
              </div>
              {expandedDiffId === v.id ? (
                <div className="mt-3 rounded-lg border border-indigo-200/60 dark:border-indigo-700/40 overflow-hidden animate-view-transition">
                  <div className="flex items-center justify-between px-3 py-1.5 bg-indigo-50/60 dark:bg-indigo-900/20 border-b border-indigo-200/40 dark:border-indigo-700/30">
                    <span className="text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">Vergleich: Version vs. Editor</span>
                    <div className="flex items-center gap-3 text-[10px]">
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-red-200 dark:bg-red-900/60 inline-block" />Version</span>
                      <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-sm bg-green-200 dark:bg-green-900/60 inline-block" />Editor</span>
                    </div>
                  </div>
                  <div className="max-h-64 overflow-y-auto font-mono text-xs leading-relaxed">
                    {(activeDiff || []).map((lineInfo, i) => (
                      <div
                        key={i}
                        className={`px-3 py-0.5 ${
                          lineInfo.type === 'added'
                            ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300'
                            : lineInfo.type === 'removed'
                              ? 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300 line-through opacity-70'
                              : lineInfo.type === 'modified'
                                ? 'bg-amber-50/50 dark:bg-amber-900/10 text-gray-800 dark:text-gray-300'
                                : 'text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        <span className="select-none mr-2 text-gray-400 font-mono">
                          {lineInfo.type === 'added' ? '+' : lineInfo.type === 'removed' ? '-' : lineInfo.type === 'modified' ? '~' : ' '}
                        </span>
                        {lineInfo.chunks.map(c => c.value).join('') === '' ? (
                          <span className="text-gray-300"> </span>
                        ) : (
                          lineInfo.chunks.map((chunk, j) => (
                            <span
                              key={j}
                              className={
                                lineInfo.type === 'modified'
                                  ? chunk.type === 'added'
                                    ? 'bg-green-200 dark:bg-green-900/60 text-green-900 dark:text-green-100 rounded px-0.5 font-medium'
                                    : chunk.type === 'removed'
                                      ? 'bg-red-200 dark:bg-red-900/60 text-red-900 dark:text-red-100 line-through rounded px-0.5 opacity-80'
                                      : ''
                                  : ''
                              }
                            >
                              {chunk.value}
                            </span>
                          ))
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <textarea
                  value={v.content}
                  onChange={(e) => {
                    handleUpdateVersion(v.id, 'content', e.target.value);
                    const el = e.target;
                    el.style.height = 'auto';
                    el.style.height = `${Math.min(el.scrollHeight, 700)}px`;
                  }}
                  placeholder="Inhalt der Version..."
                  className="mt-3 w-full min-h-40 p-3 rounded-lg bg-gray-50 dark:bg-gray-900/50 border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-indigo-500 focus:outline-none font-mono text-sm leading-6 resize-none"
                  aria-label={`Inhalt der Version ${v.name}`}
                />
              )}
            </div>
          ))
        ) : (
          <p className="text-xs text-gray-500 text-center py-4">
            Keine Versionen gespeichert. Bearbeiten Sie den Prompt und klicken Sie auf "Als Version speichern".
          </p>
        )}
      </div>

      {isModalOpen && (
        <Modal title="Neue Version speichern" onClose={() => setIsModalOpen(false)}>
          <form onSubmit={(e) => { e.preventDefault(); handleSaveNewVersion(); }}>
            <label htmlFor="versionName" className="text-sm text-gray-600 dark:text-gray-300">Versionsname</label>
            <input
              type="text"
              name="versionName"
              id="versionName"
              autoFocus
              required
              value={newVersionName}
              onChange={(e) => setNewVersionName(e.target.value)}
              className="mt-1 w-full px-3 py-2 rounded-md bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button type="button" onClick={() => setIsModalOpen(false)} className="px-4 py-2 rounded-md text-sm font-medium bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500">Abbrechen</button>
              <button type="submit" className="px-4 py-2 rounded-md text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700">Speichern</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
};

export default VersionHistory;
